import type { ContentPack, EventDefinition } from "./types.ts";
import { ADAPTED_FAMILY_EVENTS, ADAPTED_GENERATION_ARCS, ADAPTED_LIFE_EVENTS } from "./legacyContentAdapter.ts";
import { LIFE_EVENT_EXPANSION } from "./lifeEventExpansion.ts";
import { LIFE_EVENT_WAVE_TWO } from "./lifeEventWaveTwo.ts";
import { CAREER_PATH_EVENTS, EXPANDED_CAREER_ENTRY_CHOICES, LIFE_DIVERSITY_EVENTS } from "./lifeDiversityContent.ts";

const milestone = (id: string, age: number, topic: string, title: string, choices: EventDefinition["choices"]): EventDefinition => ({
  id,
  topic,
  scope: "person",
  title,
  text: topic === "education"
    ? "新的學習階段到了，家庭要在費用、壓力與成長方向之間做出安排。"
    : topic === "career"
      ? "眼前的路各有代價，也會改變接下來幾年的工作與生活。"
      : "這一年，你需要替下一段生活選一個方向。",
  weight: 100,
  once: true,
  maxOccurrences: 1,
  requiredStage: topic === "education" || topic === "career",
  conditions: { exactAge: age, ...(id === "graduate-study-choice" ? { careerStatus: "student" as const } : {}) },
  choices,
});

const CORE_LOGIC_EVENTS: EventDefinition[] = [
  {
    id: "first-clear-word",
    topic: "early-childhood",
    scope: "person",
    title: "第一個清楚的詞",
    text: "家人第一次聽懂你清楚說出的詞，並把它記進家庭紀錄。",
    weight: 40,
    once: true,
    maxOccurrences: 1,
    topicCooldownYears: 4,
    passive: true,
    conditions: { minAge: 1, maxAge: 2 },
    choices: [{ id: "remember", label: "第一次說清楚話", text: "你開始能用簡單的詞讓家人明白自己的意思。", effects: { stats: { charm: 2 }, traits: ["早期表達"], flags: ["first-word-complete"], discoverTalents: ["language"] } }],
  },
  milestone("kindergarten-choice", 3, "education", "幼兒園的選擇", [
    { id: "public", label: "社區公立幼兒園", text: "家庭資金 -35；生活穩定，容易建立同齡朋友。", effects: { stats: { empathy: 4 }, assets: -35, traits: ["社區連結"], interests: ["care"], discoverTalents: ["care"], flags: ["education-kindergarten"] } },
    { id: "bilingual", label: "私立雙語幼兒園", text: "家庭資金 -105；語言刺激較多，課表也更密集。", effects: { stats: { intellect: 3, charm: 2, health: -1 }, assets: -105, traits: ["早期語言環境"], interests: ["language"], discoverTalents: ["language"], flags: ["education-kindergarten"] } },
    { id: "explore", label: "自然探索幼兒園", text: "家庭資金 -70；大量戶外活動培養體能與好奇心。", effects: { stats: { stamina: 4, intellect: 1 }, assets: -70, traits: ["探索學習"], interests: ["nature"], discoverTalents: ["nature", "sports"], flags: ["education-kindergarten"] } },
    { id: "family", label: "家庭共學與社區托育", text: "家庭資金 -20；保留彈性與陪伴，但學習資源較分散。", effects: { stats: { empathy: 3, health: 2, charm: -1 }, assets: -20, traits: ["家庭共學"], interests: ["creative"], discoverTalents: ["creative"], flags: ["education-kindergarten"] } },
  ]),
  milestone("elementary-choice", 6, "education", "小學入學", [
    { id: "public", label: "就讀社區公立小學", text: "家庭資金 -45；課業、朋友與生活保有彈性。", effects: { stats: { empathy: 4, intellect: 2 }, assets: -45, traits: ["穩定教育"], interests: ["care"], flags: ["education-elementary"] } },
    { id: "private", label: "進入私立菁英小學", text: "家庭資金 -150；智力提升較快，健康與休息承受壓力。", effects: { stats: { intellect: 4, health: -2 }, assets: -150, traits: ["密集教育"], interests: ["science", "language"], discoverTalents: ["science"], flags: ["education-elementary"] } },
    { id: "bilingual", label: "選擇雙語特色小學", text: "家庭資金 -125；語言與表達機會增加。", effects: { stats: { intellect: 3, charm: 5, stamina: -1 }, assets: -125, traits: ["雙語學習"], interests: ["language"], discoverTalents: ["language"], flags: ["education-elementary"] } },
    { id: "alternative", label: "選擇實驗教育", text: "家庭資金 -80；以專題和實作探索自己的強項。", effects: { stats: { intellect: 2, luck: 3, empathy: 2 }, assets: -80, traits: ["自主學習"], interests: ["creative", "practical"], discoverTalents: ["creative", "practical"], flags: ["education-elementary"] } },
  ]),
  milestone("junior-choice", 12, "education", "國中階段", [
    { id: "balanced", label: "就讀學區公立國中", text: "家庭資金 -55；兼顧課業、朋友與家庭。", effects: { stats: { intellect: 3, empathy: 3 }, assets: -55, traits: ["生活平衡"], interests: ["care"], flags: ["education-junior"] } },
    { id: "elite", label: "進入私立升學國中", text: "家庭資金 -170；智力明顯提升，健康與自由時間下降。", effects: { stats: { intellect: 5, health: -4 }, assets: -170, traits: ["競爭教育"], interests: ["science", "language"], flags: ["education-junior"] } },
    { id: "bilingual", label: "選擇雙語特色國中", text: "家庭資金 -145；語言、表達與跨文化經驗增加。", effects: { stats: { intellect: 3, charm: 5, stamina: -2 }, assets: -145, traits: ["跨文化學習"], interests: ["language"], discoverTalents: ["language"], flags: ["education-junior"] } },
    { id: "specialty", label: "投入體育或藝術專班", text: "家庭資金 -105；專長快速成長，學科進度較分散。", effects: { stats: { stamina: 6, charm: 5, intellect: -1 }, assets: -105, traits: ["專長培養"], interests: ["sports", "creative"], discoverTalents: ["sports", "creative"], flags: ["education-junior"] } },
  ]),
  milestone("senior-choice", 15, "education", "高中方向", [
    { id: "academic", label: "公立普通高中", text: "家庭資金 -70；保留大學分流彈性，智力 +4。", effects: { stats: { intellect: 4 }, assets: -70, traits: ["學術準備"], interests: ["science", "language"], flags: ["education-senior"] } },
    { id: "private", label: "私立升學高中", text: "家庭資金 -185；智力 +6、健康 -4，升學準備最密集。", effects: { stats: { intellect: 6, health: -4 }, assets: -185, traits: ["升學競爭"], interests: ["science"], flags: ["education-senior"] } },
    { id: "technical", label: "技術型高中", text: "家庭資金 -55；提早累積可工作的實作技能。", effects: { stats: { stamina: 5, intellect: 2 }, assets: -55, traits: ["實作能力"], interests: ["practical"], discoverTalents: ["practical"], flags: ["education-senior"] } },
    { id: "creative", label: "藝術與設計高中", text: "家庭資金 -120；魅力與創作能力成長，未來收入較不穩定。", effects: { stats: { charm: 8, luck: 2, intellect: 1 }, assets: -120, traits: ["創作訓練"], interests: ["creative"], discoverTalents: ["creative"], flags: ["education-senior"] } },
  ]),
  milestone("university-choice", 18, "education", "大學與成年選擇", [
    { id: "university", label: "就讀公立綜合大學", text: "家庭資金 -180；延後全職收入，智力 +5、魅力 +3。", effects: { stats: { intellect: 5, charm: 3 }, assets: -180, traits: ["高等教育"], interests: ["science", "language", "business"], flags: ["education-university"] } },
    { id: "vocational", label: "就讀應用型大學", text: "家庭資金 -95；以四年實作課程取得專業技能，22 歲進入職場。", effects: { stats: { stamina: 3, intellect: 3 }, assets: -95, traits: ["專業訓練"], interests: ["practical", "business"], flags: ["education-vocational"] } },
    { id: "overseas", label: "申請海外或國際課程", text: "家庭資金 -280；語言、魅力與見識提升，家庭現金壓力最大。", effects: { stats: { intellect: 4, charm: 7, luck: 4, health: -2 }, assets: -280, traits: ["海外學習"], interests: ["language"], discoverTalents: ["language"], flags: ["education-university"] }, requires: { householdAssetsMin: 280 } },
    {
      id: "work",
      label: "直接投入技術服務工作",
      text: "18 歲起累積年資；下年度起年收入 92。",
      effects: {
        stats: { stamina: 5 },
        traits: ["提早就業"],
        flags: ["career-started"],
        career: { industry: "技術服務", occupation: "初階技術人員", status: "employed", level: 1, experience: 0, annualIncome: 92 },
      },
    },
  ]),
  {
    id: "relationship-encounter",
    topic: "relationship",
    scope: "person",
    title: "一次意外聊得來的相遇",
    text: "某個平常的日子裡，你和一個原本陌生的人多聊了一會兒。",
    weight: 14,
    repeatable: true,
    relationshipEncounter: true,
    conditions: { minAge: 14, maxAge: 58, partnered: false },
    choices: [
      { id: "approach", label: "交換聯絡方式，再約一次見面", text: "你願意讓好感慢慢長成一段交往；雙方暫時不同住，收入也不合併。", effects: { stats: { charm: 2, empathy: 1 }, traits: ["願意親近"], action: "start-dating-candidate" } },
      { id: "friends", label: "先保持朋友關係", text: "你喜歡這次談話，但不急著替關係命名。", effects: { stats: { empathy: 2 }, traits: ["珍惜友誼"] } },
      { id: "leave", label: "禮貌道別，不留下聯絡方式", text: "這次相遇停在一段愉快的談話，沒有勉強延續。", effects: { stats: { intellect: 1 } } },
    ],
  },
  {
    id: "dating-growing-pains",
    topic: "relationship",
    scope: "person",
    title: "兩人的時間開始互相擠壓",
    text: "{partner} 希望你們更常見面，但你最近也有自己的課業或工作要顧。",
    weight: 12,
    repeatable: true,
    cooldownYears: 3,
    topicCooldownYears: 1,
    maxOccurrences: 5,
    conditions: { minAge: 14, maxAge: 65, relationshipStage: "dating" },
    choices: [
      { id: "talk", label: "說清楚彼此需要，再一起排時間", text: "你們沒有假裝矛盾不存在，也沒有把所有時間交出去。", effects: { stats: { empathy: 4, health: 1 }, traits: ["關係溝通"] } },
      { id: "prioritize", label: "暫時把更多時間留給感情", text: "相處變多，自己的課業或工作進度稍微落後。", effects: { stats: { charm: 3, intellect: -2, empathy: 2 }, traits: ["投入感情"] } },
      { id: "distance", label: "堅持各自安排，不做額外調整", text: "你保住自己的節奏，對方卻感到被推遠。", effects: { stats: { intellect: 2, empathy: -3 }, traits: ["重視界線"] } },
      { id: "breakup", label: "承認彼此步調不同，和平分手", text: "你們停止交往，各自帶著這段經驗回到生活裡。", effects: { stats: { health: -1, empathy: 2 }, action: "end-relationship" } },
    ],
  },
  {
    id: "marriage-decision",
    topic: "relationship",
    scope: "person",
    title: "是否把交往帶進共同生活",
    text: "{partner} 和你談到婚姻、同住與共同承擔。結婚後才會合併家庭收入與支出。",
    weight: 22,
    repeatable: true,
    cooldownYears: 2,
    topicCooldownYears: 1,
    conditions: { minAge: 18, maxAge: 100, relationshipStage: "dating" },
    choices: [
      { id: "marry", label: "結婚並組成共同家庭戶", text: "伴侶加入同一戶；雙方收入、生活費與重要家庭選擇從此共同計算。", effects: { stats: { empathy: 3 }, traits: ["婚姻承諾"], action: "marry-partner" } },
      { id: "wait", label: "繼續交往，暫不結婚", text: "維持各自家庭與收入，之後仍可能再次討論。", effects: { stats: { empathy: 1 } } },
      { id: "separate", label: "結束這段關係", text: "雙方回到單身，沒有家庭資產移轉。", effects: { stats: { health: -1 }, action: "end-relationship" } },
    ],
  },
  {
    id: "pregnancy-decision",
    topic: "family",
    scope: "household",
    title: "得知這次懷孕",
    text: "你們需要決定接下來一年的工作、產檢與照顧安排。",
    weight: 100,
    requiredStage: true,
    choices: [
      { id: "rest", label: "完全休養", text: "媽媽下一完整年度收入為 0；生產成功率 100%。", effects: { action: "protect-pregnancy" } },
      { id: "reduced", label: "減少工作", text: "媽媽下一完整年度收入為原本的 50%；生產成功率 85%。", effects: { action: "reduce-work-pregnancy" } },
      { id: "work", label: "維持工作", text: "媽媽收入維持 100%；生產成功率 50%。", effects: { action: "continue-pregnancy" } },
      { id: "end", label: "不繼續懷孕", text: "這次懷孕結束，不產生子女；媽媽共感 -2、健康 -3。", effects: { action: "end-pregnancy" } },
    ],
  },
  {
    id: "adoption-decision",
    topic: "family",
    scope: "household",
    title: "一項需要共同承接的領養安置",
    text: "這次安置需要你先決定是否願意完整承接；孩子的年齡、性別、姓名與生活狀況會在接受後說明。",
    weight: 100,
    requiredStage: true,
    choices: [
      { id: "accept", label: "接受這次共同安置", text: "所有孩子會一起加入家庭，不能只接受其中一名。", effects: { action: "accept-adoption" } },
      { id: "decline", label: "這次無法承接", text: "這次安置交由其他家庭評估，未來仍可能再次遇到領養機會。", effects: { action: "decline-adoption" } },
    ],
  },
  {
    id: "adoption-care-decision",
    topic: "family",
    scope: "household",
    title: "安排第一年的親子培養",
    text: "孩子加入家庭後，第一年的時間與工作安排會影響彼此建立信任的方式。",
    weight: 100,
    requiredStage: true,
    choices: [
      { id: "both", label: "兩人共育", text: "兩位照顧者：下一完整年度收入 0；共感 +5、健康 +2。", effects: { action: "adoption-care-both" } },
      { id: "primary", label: "全職照顧", text: "選擇一位主要照顧者；下一完整年度收入 0，共感 +2、魅力 -2、健康 +1。", effects: { action: "adoption-care-primary" } },
      { id: "balance", label: "育兒平衡", text: "兩位照顧者：收入調整為原本的 60%；共感 +5、魅力 +3、健康 -1。", effects: { action: "adoption-care-balance" } },
      { id: "external", label: "安排外部照顧", text: "依孩子年齡安排托育、課後照顧或生活適應支援；兩位照顧者共感 -2，家庭依期間每年支出 100。", effects: { action: "adoption-care-external" } },
    ],
  },
  {
    id: "adoption-primary-caregiver",
    topic: "family",
    scope: "household",
    title: "由誰擔任主要照顧者",
    text: "你們需要確認由誰在下一完整年度暫停工作，承擔主要照顧。",
    weight: 100,
    requiredStage: true,
    choices: [
      { id: "actor", label: "由主角全職照顧", text: "主角下一完整年度收入 0；共感 +2、魅力 -2、健康 +1。", effects: { action: "adoption-primary-actor" } },
      { id: "partner", label: "由伴侶全職照顧", text: "伴侶下一完整年度收入 0；共感 +2、魅力 -2、健康 +1。", effects: { action: "adoption-primary-partner" } },
    ],
  },
  {
    id: "unmarried-parent-infant",
    topic: "family",
    scope: "person",
    title: "兩個家庭之間的幼兒照顧",
    text: "{partner} 沒有和你同住，但你們都想陪 {relative} 建立穩定的生活節奏。",
    weight: 10,
    once: true,
    maxOccurrences: 1,
    conditions: { minAge: 18, maxAge: 75, relationshipStage: "dating", hasChildren: true },
    targetRelations: ["descendant"],
    targetConditions: { minAge: 0, maxAge: 2 },
    sharedChildWithCurrentPartner: true,
    choices: [
      { id: "routine", label: "固定一起照顧的時段", text: "你們把承諾寫進每週行程，孩子逐漸熟悉兩人的陪伴。", effects: { stats: { empathy: 3, stamina: -1 } }, targetEffects: { stats: { health: 2, empathy: 2 } } },
      { id: "home", label: "以主角家庭的作息為主", text: "日常安排比較一致，另一位家長則在可行時加入。", effects: { stats: { health: 1, empathy: 1 } }, targetEffects: { stats: { health: 1 } } },
    ],
  },
  {
    id: "unmarried-parent-preschool",
    topic: "family",
    scope: "person",
    title: "第一次向孩子說明兩人的家庭",
    text: "{relative} 開始注意到你和 {partner} 不住在一起，也提出了自己的問題。",
    weight: 10,
    once: true,
    maxOccurrences: 1,
    conditions: { minAge: 18, maxAge: 75, relationshipStage: "dating", hasChildren: true },
    targetRelations: ["descendant"],
    targetConditions: { minAge: 3, maxAge: 5 },
    sharedChildWithCurrentPartner: true,
    choices: [
      { id: "plain", label: "用孩子能懂的方式說明", text: "你們不迴避，也不讓孩子覺得自己需要替大人擔心。", effects: { stats: { empathy: 3 } }, targetEffects: { stats: { empathy: 3, health: 1 } } },
      { id: "story", label: "用家庭故事慢慢回答", text: "你把不同家庭的樣子說成一段能反覆談起的故事。", effects: { stats: { charm: 2, empathy: 2 } }, targetEffects: { stats: { charm: 2 } } },
    ],
  },
  {
    id: "unmarried-parent-elementary",
    topic: "family",
    scope: "person",
    title: "學校活動要邀請誰",
    text: "{relative} 的學校活動可以邀請家人出席，你和 {partner} 需要先把安排說清楚。",
    weight: 10,
    once: true,
    maxOccurrences: 1,
    conditions: { minAge: 18, maxAge: 80, relationshipStage: "dating", hasChildren: true },
    targetRelations: ["descendant"],
    targetConditions: { minAge: 6, maxAge: 11 },
    sharedChildWithCurrentPartner: true,
    choices: [
      { id: "together", label: "兩人一起出席", text: "你們把重點放在孩子，而不是別人的眼光。", effects: { stats: { empathy: 3, charm: 1 } }, targetEffects: { stats: { charm: 3, empathy: 2 } } },
      { id: "choice", label: "讓孩子決定想邀請誰", text: "孩子不必替大人的關係維持表面，也能說出自己的需要。", effects: { stats: { empathy: 2 } }, targetEffects: { stats: { intellect: 2, empathy: 2 } } },
    ],
  },
  {
    id: "unmarried-parent-junior",
    topic: "family",
    scope: "person",
    title: "青春期開始重新理解家庭",
    text: "{relative} 不再只接受大人給的答案，想知道你和 {partner} 為何一直沒有結婚。",
    weight: 10,
    once: true,
    maxOccurrences: 1,
    conditions: { minAge: 25, maxAge: 85, relationshipStage: "dating", hasChildren: true },
    targetRelations: ["descendant"],
    targetConditions: { minAge: 12, maxAge: 14 },
    sharedChildWithCurrentPartner: true,
    choices: [
      { id: "honest", label: "誠實談關係與責任", text: "你們不把婚姻當成唯一答案，也清楚說明從未放下照顧責任。", effects: { stats: { empathy: 3, intellect: 1 } }, targetEffects: { stats: { empathy: 3, intellect: 2 } } },
      { id: "listen", label: "先聽孩子怎麼看", text: "你沒有急著辯解，而是讓孩子把困惑和不滿完整說完。", effects: { stats: { empathy: 4 } }, targetEffects: { stats: { health: 2, empathy: 2 } } },
    ],
  },
  {
    id: "unmarried-parent-senior",
    topic: "family",
    scope: "person",
    title: "成年前的一次家庭約定",
    text: "{relative} 即將走向成年，你和 {partner} 要重新確認未來如何共同參與孩子的生活。",
    weight: 10,
    once: true,
    maxOccurrences: 1,
    conditions: { minAge: 30, maxAge: 90, relationshipStage: "dating", hasChildren: true },
    targetRelations: ["descendant"],
    targetConditions: { minAge: 15, maxAge: 17 },
    sharedChildWithCurrentPartner: true,
    choices: [
      { id: "future", label: "把未來支持方式談清楚", text: "升學、工作與住處不再靠臨時協調，孩子知道遇到事情能找誰。", effects: { stats: { intellect: 2, empathy: 3 } }, targetEffects: { stats: { intellect: 3, health: 1 } } },
      { id: "independence", label: "尊重孩子安排自己的關係", text: "你們承諾提供支持，也不要求孩子替大人的關係站隊。", effects: { stats: { empathy: 3 } }, targetEffects: { stats: { charm: 3, empathy: 2 } } },
    ],
  },
  {
    id: "talent-discovery-childhood",
    topic: "growth",
    scope: "person",
    title: "玩耍裡露出的不同長處",
    text: "有些活動讓你忘了時間，有些事情則比同齡孩子更快抓到訣竅。",
    weight: 100,
    once: true,
    maxOccurrences: 1,
    passive: true,
    conditions: { exactAge: 5 },
    choices: [{ id: "notice", label: "逐漸看見長處", text: "家人開始留意你在表達、動手做與活動中的不同反應。", effects: { revealInnateTalents: ["language", "practical", "sports"] } }],
  },
  {
    id: "talent-discovery-school",
    topic: "growth",
    scope: "person",
    title: "課堂之外的投入",
    text: "一次分組任務裡，你在整理想法、照顧同伴和提出新點子時各有不同表現。",
    weight: 100,
    once: true,
    maxOccurrences: 1,
    passive: true,
    conditions: { exactAge: 10 },
    choices: [{ id: "notice", label: "逐漸看見長處", text: "更多天賦在實際合作中被看見。", effects: { revealInnateTalents: ["science", "care", "creative"] } }],
  },
  {
    id: "talent-discovery-youth",
    topic: "growth",
    scope: "person",
    title: "開始知道自己擅長什麼",
    text: "你已能分辨一時的新鮮感，和真正願意長期投入的事情。",
    weight: 100,
    once: true,
    maxOccurrences: 1,
    passive: true,
    conditions: { exactAge: 14 },
    choices: [{ id: "notice", label: "逐漸看見長處", text: "你對組織事情與觀察環境的能力有了更清楚的認識。", effects: { revealInnateTalents: ["business", "nature"] } }],
  },
  milestone("graduate-study-choice", 22, "education", "畢業後是否繼續深造", [
    { id: "public-master", label: "就讀公立研究所", text: "家庭資金 -120；再投入兩年深化專業，24 歲後進入職場。", effects: { assets: -120, stats: { intellect: 4, health: -2 }, traits: ["研究訓練"], interests: ["science"], educationYears: 2, flags: ["education-graduate"] } },
    { id: "overseas-master", label: "申請海外研究所", text: "家庭資金 -260；兩年後帶著語言與國際經驗進入職場。", effects: { assets: -260, stats: { intellect: 4, charm: 7, luck: 3, health: -3 }, traits: ["海外研究"], interests: ["language"], educationYears: 2, flags: ["education-graduate"] }, requires: { householdAssetsMin: 260 } },
    { id: "work-study", label: "先工作並在職進修", text: "維持三年半工半讀，期間年收入 98；完成後重新選擇正式職涯。", effects: { stats: { intellect: 2, health: -3 }, traits: ["在職進修"], career: { industry: "一般服務", occupation: "在職進修助理", status: "employed", level: 1, experience: 0, annualIncome: 98 }, educationYears: 3, flags: ["education-graduate", "education-work-study"] } },
    { id: "work-now", label: "不讀研究所，直接求職", text: "保留家庭資金，接著選擇職種與業界。", effects: { stats: { stamina: 2 }, traits: ["投入職場"], flags: ["education-graduate"] } },
  ]),
  {
    id: "career-entry-after-study",
    topic: "career",
    scope: "person",
    title: "完成學業後的職涯選擇",
    text: "學習階段告一段落，現在要把累積的能力帶進工作。",
    weight: 100,
    once: true,
    maxOccurrences: 1,
    requiredStage: true,
    conditions: { minAge: 22, maxAge: 64, careerStatuses: ["student", "employed"], requiredFlags: ["education-graduate"] },
    choices: EXPANDED_CAREER_ENTRY_CHOICES,
  },
  {
    id: "career-foundation",
    topic: "career",
    scope: "person",
    title: "24 歲的職涯定位",
    text: "這次選擇決定下一階段的工作方式；收入變化從下一年度帳本開始計算。",
    weight: 100,
    once: true,
    maxOccurrences: 1,
    requiredStage: true,
    conditions: { exactAge: 24, careerStatuses: ["employed", "self-employed"], minCareerExperience: 1 },
    choices: [
      { id: "specialize", label: "留在原業界深化專業", text: "職級 +1、年收入 +18；智力 +4。", effects: { stats: { intellect: 4 }, traits: ["專業深化"], flags: ["career-professional"], careerLevelDelta: 1, careerIncomeDelta: 18 } },
      { id: "management", label: "轉向組織管理職", text: "職級 +1、年收入 +24；魅力 +3、健康 -2。", effects: { stats: { charm: 3, health: -2 }, traits: ["管理路線"], flags: ["career-management"], career: { occupation: "營運與管理專員" }, careerLevelDelta: 1, careerIncomeDelta: 24 } },
      { id: "venture", label: "離職創業", text: "家庭資金 -180，改為自營業、年收入設為 86，家業等級 +1；運氣 +3、體力 -2。", effects: { stats: { luck: 3, stamina: -2 }, assets: -180, traits: ["創業者"], flags: ["career-venture"], career: { industry: "自營事業", occupation: "創業經營者", status: "self-employed", level: 1, experience: 0, annualIncome: 86 }, businessLevelDelta: 1 }, requires: { householdAssetsMin: 180 } },
    ],
  },
  milestone("wealth-foundation", 26, "wealth", "第一個長期資產配置", [
    { id: "reserve", label: "建立預備金", text: "降低風險但放棄部分報酬。", effects: { assets: -20, traits: ["財務穩健"], householdFlags: ["emergency-fund"] } },
    { id: "invest", label: "建立長期投資組合", text: "家庭資金 -100、投資等級 +1；從下一年度起每年增加 28 投資收入。", effects: { assets: -100, traits: ["長期投資者"], householdFlags: ["portfolio-started"], investmentLevelDelta: 1 }, requires: { householdAssetsMin: 100 } },
  ]),
  {
    id: "personal-health-warning",
    topic: "health",
    scope: "person",
    title: "身體發出需要處理的警訊",
    text: "疲勞和疼痛反覆出現，醫師建議你不要只靠意志力撐過去。",
    weight: 11,
    repeatable: true,
    cooldownYears: 5,
    topicCooldownYears: 2,
    maxOccurrences: 5,
    conditions: { minAge: 16, maxAge: 82 },
    choices: [
      { id: "rest", label: "請假休息並調整作息", text: "家庭資金 -20；健康 +7、體力 +4，職涯進度暫時放慢。", effects: { assets: -20, stats: { health: 7, stamina: 4 }, careerIncomeDelta: -4, traits: ["願意休息"] } },
      { id: "standard", label: "接受標準檢查與治療", text: "家庭資金 -55；健康 +10，能維持大部分日常。", effects: { assets: -55, stats: { health: 10, stamina: 2 }, traits: ["規律治療"] } },
      { id: "complete", label: "安排完整療程與復健", text: "家庭資金 -125；健康 +16、體力 +5，但花費最高。", effects: { assets: -125, stats: { health: 16, stamina: 5 }, traits: ["積極復健"] }, requires: { householdAssetsMin: 125 } },
      { id: "continue", label: "先維持工作，延後處理", text: "年收入 +8；健康 -8、體力 -4，未來仍可能再次惡化。", effects: { stats: { health: -8, stamina: -4 }, careerIncomeDelta: 8, traits: ["忽略警訊"] } },
    ],
  },
  {
    id: "family-medical-choice",
    topic: "health",
    scope: "household",
    title: "家人的健康支出",
    text: "{relative} 需要安排檢查與休養。",
    weight: 10,
    repeatable: true,
    cooldownYears: 5,
    topicCooldownYears: 2,
    maxOccurrences: 4,
    targetRelation: "parent",
    targetConditions: { minAge: 45, maxAge: 100, alive: true },
    conditions: { minAge: 18 },
    choices: [
      { id: "full", label: "完整治療", text: "支付較高費用換取恢復。", effects: { assets: -110, stats: { empathy: 3 }, householdFlags: ["health-care-priority"] }, targetEffects: { stats: { health: 6 } } },
      { id: "standard", label: "標準治療與定期回診", text: "家庭資金 -65；健康 +3，恢復需要時間。", effects: { assets: -65, stats: { empathy: 1 } }, targetEffects: { stats: { health: 3 } } },
      { id: "basic", label: "先以藥物和生活調整", text: "家庭資金 -25；短期負擔低，但健康 -2。", effects: { assets: -25 }, targetEffects: { stats: { health: -2 } } },
      { id: "delay", label: "延後治療，先處理家庭支出", text: "資金暫時保留；家人健康 -7、主角共感 -2。", effects: { stats: { empathy: -2 } }, targetEffects: { stats: { health: -7 } } },
    ],
  },
  {
    id: "career-opportunity",
    topic: "career",
    scope: "person",
    title: "職涯出現新的責任",
    text: "新的任務可能提高收入，也會壓縮生活。",
    weight: 12,
    repeatable: true,
    cooldownYears: 4,
    topicCooldownYears: 2,
    maxOccurrences: 5,
    conditions: { minAge: 22, maxAge: 62, careerStatuses: ["employed", "self-employed"], minCareerExperience: 2, maxCareerLevel: 5 },
    choices: [
      { id: "accept", label: "接受升職與新責任", text: "職級 +1、年收入 +18（下年度起）；智力 +2、健康 -2。", effects: { stats: { intellect: 2, health: -2 }, careerIncomeDelta: 18, careerLevelDelta: 1, traits: ["承擔責任"] } },
      { id: "training", label: "先接受培訓，延後升職", text: "職級不變、年收入 +8；智力 +4、健康 -1。", effects: { stats: { intellect: 4, health: -1 }, careerIncomeDelta: 8, traits: ["準備升職"] } },
      { id: "transfer", label: "轉往較適合的同級職務", text: "職級與收入不變；魅力 +2、健康 +3。", effects: { stats: { charm: 2, health: 3 }, traits: ["調整職涯"] } },
      { id: "decline", label: "維持目前職級與收入", text: "職級、年收入不變；健康 +2。", effects: { stats: { health: 2 }, traits: ["重視生活"] } },
    ],
  },
  {
    id: "business-expansion",
    topic: "wealth",
    scope: "household",
    title: "家業是否擴大規模",
    text: "擴張會提高固定現金流，也會增加營運成本與風險。",
    weight: 9,
    repeatable: true,
    cooldownYears: 5,
    topicCooldownYears: 2,
    maxOccurrences: 4,
    conditions: { minAge: 26, maxAge: 62, careerStatus: "self-employed", householdAssetsMin: 160 },
    choices: [
      { id: "expand", label: "投入資金擴張家業", text: "家庭資金 -160；先擴充設備與人力，明年開始看到第一階段結果。", effects: { assets: -160, stats: { stamina: -2, charm: 2 } }, followUp: { kind: "family-business", risk: 0.3, stages: 2 }, requires: { householdAssetsMin: 160 } },
      { id: "pilot", label: "先做小規模市場測試", text: "家庭資金 -60；風險較低，成功後成長幅度也較小。", effects: { assets: -60, stats: { intellect: 3 } }, followUp: { kind: "family-business", risk: 0.16, stages: 2 }, requires: { householdAssetsMin: 60 } },
      { id: "hold", label: "維持現有規模", text: "資金、家業等級與年度現金流不變；健康 +1。", effects: { stats: { health: 1 } } },
    ],
  },
  {
    id: "investment-volatility",
    topic: "wealth",
    scope: "household",
    title: "投資市場劇烈波動",
    text: "家庭必須決定是否在不確定中維持原計畫。",
    weight: 8,
    repeatable: true,
    cooldownYears: 5,
    topicCooldownYears: 3,
    maxOccurrences: 4,
    conditions: { minAge: 26, householdAssetsMin: 80 },
    choices: [
      { id: "hold", label: "維持配置", text: "承受短期損失，保留長期路線。", effects: { assets: -35, traits: ["風險紀律"] } },
      { id: "exit", label: "降低風險", text: "減少損失，也降低未來報酬。", effects: { assets: -15, householdFlags: ["reduced-risk"] } },
    ],
  },
];

export const LOGIC_EVENTS: EventDefinition[] = [
  ...CORE_LOGIC_EVENTS,
  ...LIFE_DIVERSITY_EVENTS,
  ...LIFE_EVENT_EXPANSION,
  ...LIFE_EVENT_WAVE_TWO,
  ...CAREER_PATH_EVENTS,
  ...ADAPTED_LIFE_EVENTS,
  ...ADAPTED_FAMILY_EVENTS,
];

export const TEN_GENERATION_CONTENT: ContentPack = {
  id: "ten-generation-life-original-content-v1",
  abilityLabels: { stamina: "體力", intellect: "智力", charm: "魅力", empathy: "共感", luck: "運氣", health: "健康" },
  events: LOGIC_EVENTS,
  generationArcs: ADAPTED_GENERATION_ARCS,
};
